import axios from 'axios'
import './axiosConfig' // Ensures interceptors are registered
import {
    ApiResourceFilter,
    ConsumerRow,
    ConsumersApiResponse,
    transformConsumerToRow,
} from './consumerService'
import type { TreeMemberRole } from './waterTreeMemberRoles'
import { debugLog } from '@/utils/debugLog'

const API_BASE_URL = import.meta.env.VITE_API_URL

export type WaterMeterRow = {
    day: string
    dailySupply: number
    dailyConsumption: number
    nightlySupply: number
    nightlyConsumption: number
}

export const formatYYYYMMDD = (date: Date) => {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
}


export const getLastMonthRange = () => {
    const end = new Date()
    end.setDate(end.getDate() - 1) // Exclude today
    const start = new Date(end)

    const d = start.getDate()
    start.setMonth(start.getMonth() - 1)


    if (start.getDate() !== d) start.setDate(0)

    return {
        startDate: formatYYYYMMDD(start),
        endDate: formatYYYYMMDD(end),
    }
}

export const getWaterMeterStats = async (params?: {
    startDate?: string
    endDate?: string
}): Promise<WaterMeterRow[]> => {
    const range = params?.startDate && params?.endDate ? params : getLastMonthRange()

    const accountId = localStorage.getItem('accountId')
    if (!accountId) return []

    const res = await axios.get(`${API_BASE_URL}/api/water/meter/${accountId}`, {
        params: {
            startDate: range.startDate,
            endDate: range.endDate,
        },
    })


    debugLog('[dashboardService] Water Meter Stats:', res.data)

    return res.data?.data ?? []
}

export type AccountDashboard = {
    accountId: number
    siteId?: number
    accountName: string
    name: string
    armCount: number
    activePercentage: number
    openTasks?: number
    inProgressTasks?: number
}

export const getAccountDashboard = async (): Promise<AccountDashboard | null> => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return null

    const res = await axios.get(`${API_BASE_URL}/api/water/account-dashboard/${accountId}`)
    debugLog('[dashboardService] Account Dashboard Response:', res.data)
    return res.data?.data ?? null
}

/** Latitude/longitude bounding box of a site, as the Gateway reports it. */
export type GeoBounds = {
  minLat: number
  minLng: number
  maxLat: number
  maxLng: number
}

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Wire-to-UI normalization for the site extent. Returns null for anything the
 * map cannot be aimed at, so the caller can say "no locations" instead of
 * pointing somewhere wrong.
 */
export const normalizeMapExtent = (raw: unknown): GeoBounds | null => {
  if (!raw || typeof raw !== 'object') return null

  const wire = raw as Record<string, unknown>
  const minLat = finiteNumber(wire.minLat)
  const minLng = finiteNumber(wire.minLng)
  const maxLat = finiteNumber(wire.maxLat)
  const maxLng = finiteNumber(wire.maxLng)

  if (minLat === null || minLng === null) return null
  if (maxLat === null || maxLng === null) return null
  if (Math.abs(minLat) > 90 || Math.abs(maxLat) > 90) return null
  if (Math.abs(minLng) > 180 || Math.abs(maxLng) > 180) return null
  if (minLat > maxLat || minLng > maxLng) return null

  // An all-zero box is the Gulf of Guinea, never a water utility: it is what a
  // missing coordinate column looks like once it is read as a number.
  if (minLat === 0 && minLng === 0 && maxLat === 0 && maxLng === 0) return null

  return { minLat, minLng, maxLat, maxLng }
}

/**
 * Bounding box of the site's premises, used to aim the map before the markers
 * arrive. Resolves to null when the site has no usable coordinates; rejects
 * when the request fails, so the caller can tell the two cases apart.
 */
export const getAccountMapExtent = async (
  accountId: string | number
): Promise<GeoBounds | null> => {
  const res = await axios.get(
    `${API_BASE_URL}/api/water/account-dashboard/${accountId}/extent`
  )
  return normalizeMapExtent(res.data?.data)
}

/**
 * Get the account logo URL from the server.
 * The backend serves logos at: images/{tenant}_{site_id}.png
 * 
 * Logic:
 * 1. Base URL + /images/
 * 2. Tenant prefix:
 *    - If hostname is simple/IP/localhost -> 'main'
 *    - If hostname has subdomain (e.g. 'rimonim.dashboard.com') -> 'rimonim'
 * 3. Underscore '_'
 * 4. Site ID (accountId from logic) + .png
 */
export type WaterTreeZone = {
    id: number
    zoneName: string
    // Backend sends a String: 'H' + billing_id for physical trees, 'G' + group_id
    // for groups (WaterTreeRepo zones CTE) — not a numeric value.
    number?: string | null
    zoneType: number | string
    zoneTypeId?: number | null
    zoneTypeName?: string | null
    mainAddress?: string | null
    suppliers: number
    consumers: number
    supplied: number
    suppliedNight: number
    consumed: number
    consumedNight: number
    sharedConsumption: number
    lossPercent: number
    nightLossPercent: number
}

export type WaterTreeApiFilter = {
    apiResources: { name: string; value: string[]; operator?: string }[]
    logicalOperator: 'AND' | 'OR'
}

export type GetWaterTreeZonesParams = {
    accountId: string | number
    page?: number
    size?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
}

export const getWaterTreeZones = async (
    params: GetWaterTreeZonesParams,
    filters: WaterTreeApiFilter['apiResources'] = [],
    signal?: AbortSignal
): Promise<{ rows: WaterTreeZone[]; total: number; page: number; pageSize: number }> => {
    const { accountId, page = 1, size = 25, sortBy = 'zoneName', sortOrder = 'ASC' } = params
    const body: WaterTreeApiFilter = { apiResources: filters, logicalOperator: 'AND' }

    const res = await axios.post(
        `${API_BASE_URL}/api/water-tree/${accountId}?page=${page}&size=${size}&sortBy=${sortBy}&sortOrder=${sortOrder}`,
        body,
        { signal }
    )

    debugLog('[water-tree] zones (paginated) response:', res.data)

    const data = res.data?.data ?? {}
    return {
        rows: (data.result ?? []).map((r: any, idx: number) => ({
            id: r.id ?? idx,
            ...r,
            mainAddress:
                r.mainAddress ??
                r.main_address ??
                r.primaryAddress ??
                r.primary_address ??
                r.address ??
                null,
        })),
        total: data.total ?? 0,
        page: data.page ?? page,
        pageSize: data.pageSize ?? size,
    }
}

export type WaterTreeZoneListItem = {
    id: number
    name: string
    zoneType?: number | string
    zoneTypeId?: number
}

export const getWaterTreeZonesList = async (accountId: string | number): Promise<WaterTreeZoneListItem[]> => {
    const res = await axios.get(`${API_BASE_URL}/api/water-tree/${accountId}`)
    debugLog('[water-tree] zones list (GET) response:', res.data)
    const data = res.data?.data
    const rows = Array.isArray(data) ? data : data?.result ?? data?.collection?.result ?? []
    return rows.map((item: any) => ({
        id: Number(item.id ?? item.zoneId),
        name: String(item.name ?? item.zoneName ?? item.id ?? ''),
        zoneType: item.zoneType ?? item.type,
        zoneTypeId: item.zoneTypeId != null ? Number(item.zoneTypeId) : undefined,
    })).filter((item: WaterTreeZoneListItem) => Number.isFinite(item.id) && item.name)
}

/**
 * Identity of one water tree, for the details-page header.
 * parentBillingId / parentMeterNumber are the supplying premise's water card and
 * current meter number; both are null for groups (zoneType 2).
 */
export type WaterTreeHeader = {
    zoneId: number
    zoneType: number
    zoneName: string
    number?: string | null
    zoneTypeName?: string | null
    parentBillingId?: number | null
    parentMeterNumber?: number | null
}

/**
 * GET /api/water-tree/{accountId}/{zoneId}/header — the real tree name plus the
 * parent water card and meter number, so the details page no longer depends on
 * a `?treeName=` query param or hardcoded meter numbers.
 * Returns null when the zone is not found (404).
 */
export const getWaterTreeHeader = async (
    accountId: string | number,
    zoneId: string | number,
    zoneType: number | string = 2
): Promise<WaterTreeHeader | null> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/header`,
            { params: { zoneType } }
        )
        return res.data?.data ?? null
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return null
        throw err
    }
}

export type WaterTreeZoneStats = {
    minConsumption: number
    maxConsumption: number
    suppliers: number
    consumers: number
}

export type WaterTreeZonePeriod = {
    startDate: string
    endDate: string
    suppliers: number
    consumers: number
    supply: number
    supplyNight: number
    consumption: number
    consumptionNight: number
    loss: number
    lossPercent: number
    shared: number
}

export type PeriodGranularity = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'

export const getWaterTreeZoneStats = async (
    accountId: string | number,
    zoneId: number | string,
    params?: { zoneType?: 1 | 2; startDate?: string; endDate?: string; signal?: AbortSignal }
): Promise<WaterTreeZoneStats | null> => {
    const range = params?.startDate && params?.endDate
        ? { startDate: params.startDate, endDate: params.endDate }
        : getLastMonthRange()

    const res = await axios.get(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/stats`,
        {
            params: { zoneType: params?.zoneType ?? 1, startDate: range.startDate, endDate: range.endDate },
            signal: params?.signal,
        }
    )
    debugLog(`[water-tree] zone ${zoneId} stats response:`, res.data, '| range:', range)
    return res.data?.data ?? null
}

export const getWaterTreeZonePeriods = async (
    accountId: string | number,
    zoneId: number | string,
    params?: {
        zoneType?: 1 | 2
        startDate?: string
        endDate?: string
        period?: PeriodGranularity
        page?: number
        size?: number
        sortBy?: string
        sortOrder?: 'ASC' | 'DESC'
        signal?: AbortSignal
    },
    filters: WaterTreeApiFilter['apiResources'] = []
): Promise<{ rows: WaterTreeZonePeriod[]; total: number; page: number; pageSize: number }> => {
    const range = params?.startDate && params?.endDate
        ? { startDate: params.startDate, endDate: params.endDate }
        : getLastMonthRange()

    const body: WaterTreeApiFilter = { apiResources: filters, logicalOperator: 'AND' }

    const queryParams = {
        zoneType: params?.zoneType ?? 1,
        startDate: range.startDate,
        endDate: range.endDate,
        period: params?.period ?? 'MONTHLY',
        page: params?.page ?? 1,
        size: params?.size ?? 25,
        sortBy: params?.sortBy ?? 'startDate',
        sortOrder: params?.sortOrder ?? 'DESC',
    }

    debugLog(`[water-tree] zone ${zoneId} periods request:`, { params: queryParams, body })

    const res = await axios.post(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/periods`,
        body,
        { params: queryParams, signal: params?.signal }
    )

    debugLog(`[water-tree] zone ${zoneId} periods response:`, res.data)

    const data = res.data?.data ?? {}
    return {
        rows: Array.isArray(data.result) ? data.result : [],
        total: data.total ?? 0,
        page: data.page ?? 1,
        pageSize: data.pageSize ?? 25,
    }
}

export const getWaterTreeZoneChartStats = async (
    accountId: string | number,
    zoneId: number | string,
    params?: { zoneType?: 1 | 2; startDate?: string; endDate?: string; signal?: AbortSignal }
): Promise<WaterMeterRow[]> => {
    const range = params?.startDate && params?.endDate
        ? { startDate: params.startDate, endDate: params.endDate }
        : getLastMonthRange()
    const zoneType = params?.zoneType ?? 1

    const res = await axios.get(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/chart`,
        {
            params: { zoneType, startDate: range.startDate, endDate: range.endDate },
            signal: params?.signal,
        }
    )

    debugLog(`[water-tree] zone ${zoneId} chart response:`, res.data, '| range:', range)
    return res.data?.data ?? []
}

export type WaterTreeZoneType = { id: number; name: string }

export const getWaterTreeZoneTypes = async (accountId: string | number): Promise<WaterTreeZoneType[]> => {
    const res = await axios.get(`${API_BASE_URL}/api/water-tree/${accountId}/zone-types`)
    debugLog('[water-tree] zone-types response:', res.data)
    return res.data?.data ?? []
}

export const createWaterTree = async (
    accountId: string | number,
    body: { name: string; zoneTypeId: number | null; copyFromId?: number | null }
): Promise<void> => {
    await axios.post(`${API_BASE_URL}/api/water-tree/${accountId}/create`, body)
}

export const updateWaterTree = async (
    accountId: string | number,
    zoneId: number,
    body: { name: string; zoneTypeId: number | null },
    zoneType: 1 | 2 = 2
): Promise<void> => {
    await axios.put(`${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}`, body, {
        params: { zoneType },
    })
}

export const deleteWaterTree = async (
    accountId: string | number,
    zoneId: number,
    zoneType: 1 | 2 = 2
): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}`, {
        params: { zoneType },
    })
}

export type WaterTreeConsumerSearchResult = {
    premise_id: number
    billing_id: number
    meter_number: number
    payer_name: string
    address: string
}

export const searchWaterTreeConsumers = async (
    accountId: string | number,
    zoneId: number | string,
    q: string,
    limit = 20,
    zoneType: 1 | 2 = 2,
    signal?: AbortSignal
): Promise<WaterTreeConsumerSearchResult[]> => {
    const res = await axios.get(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers/search`,
        { params: { q, limit, zoneType }, signal }
    )
    debugLog(`[water-tree] consumers search (zone ${zoneId}, q="${q}") response:`, res.data)
    const data = res.data?.data
    const rows = Array.isArray(data)
        ? data
        : data?.result ?? data?.collection?.result ?? data?.content ?? []

    return rows.map((item: any) => ({
        premise_id: Number(item.premise_id ?? item.premiseId ?? item.id),
        billing_id: Number(item.billing_id ?? item.billingId ?? 0),
        meter_number: Number(item.meter_number ?? item.meterNumber ?? 0),
        payer_name: String(item.payer_name ?? item.payerName ?? item.name ?? ''),
        address: String(item.address ?? ''),
    })).filter((item: WaterTreeConsumerSearchResult) => Number.isFinite(item.premise_id))
}

export const addWaterTreeConsumers = async (
    accountId: string | number,
    zoneId: number | string,
    premiseIds: number[],
    role: TreeMemberRole,
    zoneType: 1 | 2 = 2
): Promise<number> => {
    const res = await axios.post(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers/add`,
        { premiseIds, role },
        { params: { zoneType } }
    )
    debugLog(`[water-tree] add consumers (zone ${zoneId}) response:`, res.data)
    const data = res.data?.data
    const count = Number(data?.count ?? data?.addedCount ?? data)
    return Number.isFinite(count) ? count : premiseIds.length
}

export const setWaterTreeConsumersRole = async (
  accountId: string | number,
  zoneId: number | string,
  premiseIds: number[],
  role: TreeMemberRole,
  zoneType: 1 | 2
): Promise<number> => {
  const res = await axios.put(
    `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers/role`,
    { premiseIds, role },
    { params: { zoneType } }
  )
  return Number(res.data?.data)
}

export type GetZoneConsumersParams = {
    accountId: string | number
    zoneId: number | string
    zoneType?: 1 | 2
    page?: number
    size?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
    langKey?: string
}

// The tab's columns carry the compatibility names of transformConsumerToRow. The Gateway
// filters on its own field names and drops a name it does not know without saying so.
const ZONE_CONSUMER_FILTER_FIELDS: Record<string, string> = {
    waterCard: 'billingId',
    name: 'payerName',
    payerNumber: 'payerId',
    capacity: 'lastValue',
    area: 'locationId',
}

export const getWaterTreeZoneConsumers = async (
    params: GetZoneConsumersParams,
    filters: ApiResourceFilter[] = [],
    signal?: AbortSignal
): Promise<{ rows: ConsumerRow[]; total: number; page: number; pageSize: number }> => {
    const {
        accountId, zoneId,
        zoneType = 2,
        page = 1, size = 25,
        sortBy = 'premiseId', sortOrder = 'DESC',
        langKey = 'he',
    } = params

    const body = {
        apiResources: filters.map(f => ({ ...f, name: ZONE_CONSUMER_FILTER_FIELDS[f.name] ?? f.name })),
        logicalOperator: 'AND',
    }

    const res = await axios.post<ConsumersApiResponse>(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers`,
        body,
        { params: { zoneType, page, size, sortBy, sortOrder, langKey }, signal }
    )

    debugLog(`[water-tree] zone ${zoneId} consumers response:`, res.data)

    let body2: any = res.data
    while (typeof body2 === 'string') {
        try { body2 = JSON.parse(body2) } catch { break }
    }

    const data = body2?.data
    const resultArray = data?.result
        ?? data?.collection?.result
        ?? data?.content
        ?? (Array.isArray(data) ? data : [])

    return {
        rows: (resultArray ?? []).map(transformConsumerToRow),
        total: data?.total ?? data?.collection?.total ?? resultArray.length ?? 0,
        page: data?.page ?? data?.collection?.page ?? page,
        pageSize: data?.pageSize ?? data?.collection?.pageSize ?? size,
    }
}

export const removeWaterTreeZoneConsumer = async (
    accountId: string | number,
    zoneId: number | string,
    premiseId: number,
    zoneType: 1 | 2 = 2
): Promise<void> => {
    await axios.delete(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers/${premiseId}`,
        { params: { zoneType } }
    )
}

export type HierarchyRelationship = 'parent' | 'child' | 'alternative_parent'

export type WaterTreeHierarchyNode = {
    premiseId: number
    relationship: HierarchyRelationship
    nodeName: string
    hierarchyNodeTypeId: number
    nodeNotes?: string
    nodeOpsNotes?: string
}

export const getWaterTreeHierarchyMap = async (
    accountId: string | number,
    zoneId: number | string
): Promise<WaterTreeHierarchyNode[]> => {
    const res = await axios.get(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/hierarchy-map`
    )
    debugLog(`[water-tree] hierarchy-map (zone ${zoneId}) response:`, res.data)
    return res.data?.data ?? []
}

export const removeWaterTreeConsumers = async (
    accountId: string | number,
    zoneId: number | string,
    premiseIds: number[],
    zoneType: 1 | 2 = 2
): Promise<number> => {
    const res = await axios.post(
        `${API_BASE_URL}/api/water-tree/${accountId}/${zoneId}/consumers/remove`,
        premiseIds,
        { params: { zoneType } }
    )
    debugLog(`[water-tree] remove consumers (zone ${zoneId}) response:`, res.data)
    const data = res.data?.data
    const count = Number(data?.count ?? data?.removedCount ?? data)
    return Number.isFinite(count) ? count : premiseIds.length
}

export const getWaterTreeChartStats = async (
    accountId: string | number,
    params?: { startDate?: string; endDate?: string }
): Promise<WaterMeterRow[]> => {
    const range = params?.startDate && params?.endDate ? params : getLastMonthRange()

    const res = await axios.get(`${API_BASE_URL}/api/water-tree/${accountId}/chart`, {
        params: {
            startDate: range.startDate,
            endDate: range.endDate,
        },
    })

    debugLog('[water-tree] city chart response:', res.data, '| range:', range)
    return res.data?.data ?? []
}

export const getAccountLogoUrl = (accountId: string | number): string => {
    // Extract tenant from hostname
    const hostname = window.location.hostname
    const parts = hostname.split('.')

    // Default to 'main' if localhost, IP, or no subdomain
    let tenant = 'main'

    // If we have subdomains (e.g. tenant.domain.com)
    if (parts.length >= 3) {
        // Basic check to exclude www
        if (parts[0] !== 'www') {
            tenant = parts[0]
        }
    }

    return `/images/${tenant}_${accountId}.png`
}
